/**
 * The sauna chimney the BOM sells and the 3D model draws: Harvia's steel
 * chimney system, the one Harvia sells for its wood-fired sauna stoves. Every
 * wood heater in `heaterCatalog` (Harvia M3, Legend 150 and their SL models)
 * has a Ø115 flue collar. One owner for the sizes the quote, the roof-bay rule
 * and the 3D model all use.
 *
 * Harvia WHP1500 installation manual and product data (Bygghemma, Bauhaus):
 *   - a Ø115 stainless smoke pipe from the stove collar, 1.0 m in the kit,
 *     cut to length on site;
 *   - the insulated steel chimney, Ø220 outside: the WHP1500 base section
 *     (1.5 m), WHP1000 extensions (970 mm insulated) and WHP500 (470 mm);
 *   - the insulated chimney shows at least 430 mm below the ceiling;
 *   - at most 5 m in total and 2 m free-standing above the last support.
 *
 * This replaced Contura Premodul (Ø150 flue, Ø226/Ø280 mantles) on
 * 2026-10-08: a house chimney for braskaminer, drawn about a quarter wider
 * than the chimney a sauna builder buys for these stoves.
 */
export const CHIMNEY_INNER_DIAMETER_MM = 115;
/** The uninsulated smoke pipe between the stove collar and the chimney. */
export const CHIMNEY_SMOKE_PIPE_DIAMETER_MM = 115;
export const CHIMNEY_OUTER_DIAMETER_MM = 220;
export const CHIMNEY_BASE_LENGTH_MM = 1500;
export const CHIMNEY_EXTENSION_LONG_MM = 970;
export const CHIMNEY_EXTENSION_SHORT_MM = 470;
/** The kit's smoke pipe; the chimney comes down to meet it, never further up. */
export const SMOKE_PIPE_MAX_MM = 1000;
export const CHIMNEY_BELOW_CEILING_MIN_MM = 430;
/** Harvia's maximum total height for the system. */
export const CHIMNEY_MAX_TOTAL_HEIGHT_MM = 5000;
/**
 * The smoke pipe is never cut shorter than this; when the chimney sections
 * would reach further down, the outlet rides that much higher instead. A
 * practical judgement, not a manufacturer figure.
 */
export const MIN_SMOKE_PIPE_MM = 150;

/**
 * Height of the stove's flue collar above the finished floor. The pipe run
 * starts HERE, not at floor level — the stove body carries the first ~0.6–0.8 m
 * of the total chimney height on its own.
 *
 * CONSERVATIVE ASSUMPTION: the engine's rendered wood stove is 710 mm tall
 * and typical Nordic sauna stoves (Harvia M3, Narvi NC, Kota Luosto) sit
 * between 700 and 800 mm. We deliberately assume a LOWER collar than any of
 * them so the purchased pipe run is never short.
 */
export const DEFAULT_FLUE_OUTLET_HEIGHT_MM = 600;

export interface ChimneyStack {
	/** Required total height, finished floor → flue outlet. */
	heightMm: number;
	/** Finished floor → stove flue collar, where the smoke pipe starts. */
	flueOutletHeightMm: number;
	/** WHP1000 extensions bought on top of the base section. */
	extensionsLong: number;
	/** WHP500 extensions bought on top of the base section. */
	extensionsShort: number;
	/** The insulated sections as installed, bottom → top, base first. */
	sectionsMm: number[];
	/** Finished floor → the bottom of the insulated chimney. */
	chimneyBottomMm: number;
	/** The smoke pipe as cut, collar → chimney. */
	smokePipeMm: number;
	/** Finished floor → the flue outlet as installed (≥ heightMm). */
	topMm: number;
}

/**
 * The fewest sections that reach from the outlet down to the smoke pipe: the
 * insulated chimney comes down at least to `CHIMNEY_BELOW_CEILING_MIN_MM`
 * under the ceiling and to where the 1 m smoke pipe ends; extra length hangs
 * further into the room and the smoke pipe is cut shorter, so the outlet lands
 * on the required height.
 */
export function planChimneyStack(
	heightMm: number,
	/** Finished floor → the ceiling the chimney passes into. */
	ceilingMm: number,
	flueOutletHeightMm: number = DEFAULT_FLUE_OUTLET_HEIGHT_MM,
): ChimneyStack {
	const highestBottomMm = Math.min(
		ceilingMm - CHIMNEY_BELOW_CEILING_MIN_MM,
		flueOutletHeightMm + SMOKE_PIPE_MAX_MM,
	);
	const neededMm = heightMm - highestBottomMm;
	let best = { long: 0, short: 0, lengthMm: CHIMNEY_BASE_LENGTH_MM };
	if (neededMm > CHIMNEY_BASE_LENGTH_MM) {
		const candidates = [];
		for (let long = 0; long <= 10; long += 1) {
			for (const short of [0, 1]) {
				const lengthMm =
					CHIMNEY_BASE_LENGTH_MM +
					long * CHIMNEY_EXTENSION_LONG_MM +
					short * CHIMNEY_EXTENSION_SHORT_MM;
				if (lengthMm >= neededMm) candidates.push({ long, short, lengthMm });
			}
		}
		// Shortest that reaches; a long extension beats two pieces at equal length.
		candidates.sort((a, b) => a.lengthMm - b.lengthMm || a.short - b.short);
		best = candidates[0] ?? best;
	}
	const chimneyBottomMm = Math.max(
		heightMm - best.lengthMm,
		flueOutletHeightMm + MIN_SMOKE_PIPE_MM,
	);
	return {
		heightMm,
		flueOutletHeightMm,
		extensionsLong: best.long,
		extensionsShort: best.short,
		sectionsMm: [
			CHIMNEY_BASE_LENGTH_MM,
			...Array<number>(best.long).fill(CHIMNEY_EXTENSION_LONG_MM),
			...Array<number>(best.short).fill(CHIMNEY_EXTENSION_SHORT_MM),
		],
		chimneyBottomMm,
		smokePipeMm: chimneyBottomMm - flueOutletHeightMm,
		topMm: chimneyBottomMm + best.lengthMm,
	};
}
